/**
 * Kundenliste im Entwurf B (Benjamin, 03.11.2026: „Die Karten für die Kunden sind
 * viel zu groß"). Eine Zeile je Kunde: Kreis mit Kürzel, Name und der eine Satz,
 * was gerade ansteht (`lib/kundeLage.ts`). Oben die Suche, die zuletzt
 * kontaktierten Kunden und Filter; rechts eine Buchstabenleiste.
 *
 * Die Pflegeübersicht nach Monaten ist der Filter „Pflege" mit Monatswahl —
 * vorher war sie eine eigene Tabelle hinter einem Umschalter.
 */
import { useMemo, useState } from "react";
import type { Kunde } from "../../lib/types";
import { parseKontakte } from "../../lib/kundeAnlage";
import { MONATE, denormAddr, normAddr } from "../../lib/kundeFormular";
import { brauchtDich, buchstabe, hinweis, kreisTon, kuerzel, type KundeLage } from "../../lib/kundeLage";
import { TelefonLink, kundeHauptnummer } from "./KontaktAktionen";
import { ZeichenTelefon } from "./KundeZeichen";

type Filter = "alle" | "pflege" | "firmen" | "braucht";
type Props = {
  kunden: Kunde[];
  lage: Map<string, KundeLage>;
  laedt: boolean;
  fehler: boolean;
  gewaehlt: string;
  onWaehlen: (k: Kunde) => void;
  onNeu: () => void;
};

function ortVon(k: Kunde): string {
  return (k.ort || normAddr(k.lieferadresse || k.adresse || "").ort || "").trim();
}

function suchtext(k: Kunde): string {
  const kontakte = parseKontakte(k.kontakte_json || "[]").map((c) => `${c.name} ${c.role} ${c.email} ${c.phone}`).join(" ");
  const anschrift = denormAddr(k.strasse || "", k.plz || "", k.ort || "") || k.lieferadresse || k.adresse || "";
  return `${k.name} ${k.id} ${k.email} ${k.phone} ${anschrift} ${kontakte} ${k.notes || ""} ${Object.values(k.pflege_monate || {}).join(" ")}`.toLowerCase();
}

export default function KundenListe({ kunden, lage, laedt, fehler, gewaehlt, onWaehlen, onNeu }: Props) {
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState<Filter>("alle");
  const [monat, setMonat] = useState<number>(() => new Date().getMonth() + 1);

  const zahlen = useMemo(() => ({
    pflege: kunden.filter((k) => Object.keys(k.pflege_monate || {}).length > 0).length,
    firmen: kunden.filter((k) => k.kundentyp === "firma").length,
    braucht: kunden.filter((k) => brauchtDich(lage.get(k.id))).length,
  }), [kunden, lage]);

  const zuletzt = useMemo(() => kunden
    .filter((k) => lage.get(k.id)?.zuletzt)
    .sort((a, b) => (lage.get(b.id)?.zuletzt || "").localeCompare(lage.get(a.id)?.zuletzt || ""))
    .slice(0, 6), [kunden, lage]);

  const monatKey = String(monat);
  const zeilen = useMemo(() => {
    const s = q.trim().toLowerCase();
    return kunden
      .filter((k) => !s || suchtext(k).includes(s))
      .filter((k) => filter !== "pflege" || Object.prototype.hasOwnProperty.call(k.pflege_monate || {}, monatKey))
      .filter((k) => filter !== "firmen" || k.kundentyp === "firma")
      .filter((k) => filter !== "braucht" || brauchtDich(lage.get(k.id)))
      .sort((a, b) => (a.name || "").localeCompare(b.name || "", "de", { sensitivity: "base" }));
  }, [kunden, lage, q, filter, monatKey]);

  const gruppen = useMemo(() => {
    const m = new Map<string, Kunde[]>();
    for (const k of zeilen) {
      const b = buchstabe(k.name);
      m.set(b, [...(m.get(b) ?? []), k]);
    }
    return [...m.entries()];
  }, [zeilen]);

  const proMonat = useMemo(() => {
    const c: Record<number, number> = {};
    for (const k of kunden) for (const mk of Object.keys(k.pflege_monate || {})) c[+mk] = (c[+mk] ?? 0) + 1;
    return c;
  }, [kunden]);

  const springen = (b: string) => document.getElementById(`ku-b-${b}`)?.scrollIntoView({ block: "start", behavior: "smooth" });
  const chips: { id: Filter; label: string; zahl: number | null }[] = [
    { id: "alle", label: "Alle", zahl: null },
    { id: "braucht", label: "Braucht dich", zahl: zahlen.braucht },
    { id: "pflege", label: "Pflege", zahl: zahlen.pflege },
    { id: "firmen", label: "Firmen", zahl: zahlen.firmen },
  ];

  return (
    <div className="ku-liste">
      <div className="ku-suche">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>
        <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Wen suchst du?" aria-label="Kunden suchen — Name, Ort, Telefon, Ansprechpartner" />
        <button type="button" className="ku-neu" aria-label="Neuer Kunde" title="Neuer Kunde" onClick={onNeu}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true"><path d="M12 5v14M5 12h14" /></svg>
        </button>
      </div>

      {!q.trim() && filter === "alle" && zuletzt.length > 0 && (
        <section className="ku-zuletzt" aria-label="Zuletzt im Kontakt">
          <h3>Zuletzt im Kontakt</h3>
          <div className="ku-zuletzt-reihe">
            {zuletzt.map((k) => (
              <button key={k.id} type="button" className="ku-zuletzt-kunde" onClick={() => onWaehlen(k)} title={k.name}>
                <span className={`ku-kreis is-gross is-${kreisTon(k.id)}`} aria-hidden="true">{kuerzel(k.name)}</span>
                <span className="ku-zuletzt-name">{(k.kundentyp === "privat" && k.vorname) || k.name.split(/\s+/)[0]}</span>
              </button>
            ))}
          </div>
        </section>
      )}

      <div className="ku-filter" role="group" aria-label="Filter">
        {chips.map((c) => (
          <button key={c.id} type="button" className={"ku-chip" + (filter === c.id ? " ist-an" : "")} aria-pressed={filter === c.id} onClick={() => setFilter(c.id)}>
            {c.label}{c.zahl !== null && <span className="ku-chip-zahl">{c.zahl}</span>}
          </button>
        ))}
      </div>

      {filter === "pflege" && (
        <div className="ku-monat">
          <button type="button" className="ku-rund" aria-label="Voriger Monat" onClick={() => setMonat((m) => ((m + 10) % 12) + 1)}>‹</button>
          <select value={monatKey} onChange={(e) => setMonat(parseInt(e.target.value, 10))} aria-label="Pflegemonat">
            {MONATE.map(([n, lang]) => <option key={n} value={String(n)}>{lang} ({proMonat[n] ?? 0})</option>)}
          </select>
          <button type="button" className="ku-rund" aria-label="Nächster Monat" onClick={() => setMonat((m) => (m % 12) + 1)}>›</button>
        </div>
      )}

      <div className="ku-koerper">
        <div className="ku-zeilen">
          {laedt && <p className="ku-leer">Lade Kunden …</p>}
          {fehler && <p className="ku-leer is-fehler">Kunden konnten nicht geladen werden.</p>}
          {!laedt && !fehler && zeilen.length === 0 && (
            <p className="ku-leer">{filter === "pflege" ? "In diesem Monat ist keine Pflege eingetragen." : "Niemand gefunden."}</p>
          )}
          {gruppen.map(([b, liste]) => (
            <div key={b} className="ku-gruppe">
              <h3 className="ku-buchstabe" id={`ku-b-${b}`}>{b}</h3>
              {liste.map((k) => {
                const l = lage.get(k.id);
                const h = filter === "pflege"
                  ? { text: (k.pflege_monate?.[monatKey] || "").trim() || "Pflege", ton: "pflege" as const }
                  : hinweis(k, l, ortVon(k));
                const nummer = kundeHauptnummer(k);
                return (
                  <div key={k.id} className={"ku-zeile" + (gewaehlt === k.id ? " ist-gewaehlt" : "")} data-kunde-zeile={k.id}>
                    <button type="button" className="ku-zeile-haupt" onClick={() => onWaehlen(k)} aria-current={gewaehlt === k.id ? "true" : undefined}>
                      <span className={`ku-kreis is-${kreisTon(k.id)}`} aria-hidden="true">{kuerzel(k.name)}</span>
                      <span className="ku-zeile-text">
                        <span className="ku-zeile-name">{k.name || "Ohne Namen"}</span>
                        {h.text && <span className={`ku-hinweis is-${h.ton || "ort"}`}>{h.text}</span>}
                      </span>
                      {h.ton && h.ton !== "pflege" && <span className={`ku-punkt is-${h.ton}`} aria-hidden="true" />}
                    </button>
                    {nummer && <TelefonLink nummer={nummer} className="ku-rund ku-anruf"><ZeichenTelefon /></TelefonLink>}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
        {gruppen.length > 6 && (
          <nav className="ku-abc" aria-label="Zu einem Buchstaben springen">
            {gruppen.map(([b]) => <button key={b} type="button" onClick={() => springen(b)}>{b}</button>)}
          </nav>
        )}
      </div>
    </div>
  );
}
